<template>
  <div class="receipt-print-page">
    <div class="print-toolbar no-print">
      <el-button icon="el-icon-close" @click="closePage">关闭</el-button>
      <el-button
        type="primary"
        icon="el-icon-printer"
        :disabled="!canPrint"
        @click="printPage"
      >打印</el-button>
    </div>

    <div v-if="loading" class="page-state no-print">
      <i class="el-icon-loading state-icon"></i>
      <span>正在加载收据数据...</span>
    </div>

    <div v-else-if="errorMessage" class="page-state page-error no-print">
      <i class="el-icon-warning-outline state-icon"></i>
      <span>{{ errorMessage }}</span>
      <el-button v-if="receiptNumber" type="primary" plain @click="loadReceipt">重新加载</el-button>
    </div>

    <main v-else class="receipt-document">
      <section
        v-for="(page, pageIndex) in receiptPages"
        :key="pageIndex"
        class="receipt-sheet"
      >
        <article
          v-for="copy in receiptCopies"
          :key="copy.key"
          class="receipt-copy"
          :class="fontLevelClass(page.fontLevel)"
        >
          <header class="receipt-header">
            <div class="brand-placeholder">
              <img
                class="brand-image"
                src="@/assets/imgs/receipt-yh-logo.png"
                alt="怡和物业 YIHE PROPERTY"
              >
            </div>

            <div class="receipt-title">
              <h1>收 款 收 据</h1>
              <span>{{ displayValue(receiptInfo.receiptDate) }}</span>
            </div>

            <div class="receipt-number">
              <span class="copy-label">{{ copy.label }}</span>
              <span v-if="receiptPages.length > 1" class="page-number">
                第 {{ pageIndex + 1 }}/{{ receiptPages.length }} 页
              </span>
              <strong>NO. {{ displayValue(receiptInfo.billNo) }}</strong>
            </div>
          </header>

          <div class="receipt-base-info">
            <div><span>项目：</span>{{ displayValue(receiptInfo.gardenName) }}</div>
            <div><span>房号：</span>{{ displayValue(receiptInfo.roomName) }}</div>
            <div><span>面积：</span>{{ formatArea(receiptInfo.buildingArea) }}</div>
          </div>

          <div class="customer-row">
            <span>客户名称：</span>{{ displayValue(receiptInfo.customerName) }}
          </div>

          <table class="receipt-table">
            <colgroup>
              <col class="fee-name-column">
              <col class="period-column">
              <col class="summary-column">
              <col class="amount-column">
              <col class="item-remark-column">
            </colgroup>
            <thead>
              <tr>
                <th>收费项目</th>
                <th>费用应收期间</th>
                <th>缴费摘要</th>
                <th>实收金额</th>
                <th>备注</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, rowIndex) in page.rows" :key="rowIndex" class="detail-row">
                <td>{{ displayValue(item.feeItemName) }}</td>
                <td>{{ displayValue(item.feeReceivablePeriod) }}</td>
                <td>{{ displayValue(item.paymentSummary) }}</td>
                <td class="money-cell">{{ formatMoney(item.receivedAmount) }}</td>
                <td class="item-remark-cell">{{ detailRemark(item) }}</td>
              </tr>
              <tr class="total-row">
                <th>{{ receiptPages.length > 1 ? '整单合计金额' : '合计收款金额' }}</th>
                <td colspan="2">
                  人民币（大写）：{{ amountToChinese(receiptInfo.totalReceivedAmount) }}
                </td>
                <td class="money-cell">{{ formatMoney(receiptInfo.totalReceivedAmount) }}</td>
                <td></td>
              </tr>
              <!-- <tr class="remark-row">
                <th>实收备注</th>
                <td colspan="4">{{ displayValue(receiptInfo.receiptRemark) }}</td>
              </tr> -->
            </tbody>
          </table>

          <footer class="receipt-footer">
            <span class="receipt-payee-unit">
              <span>收款单位：（盖章有效）</span>
              <img
                class="receipt-stamp"
                :src="receiptStamp.src"
                :alt="receiptStamp.alt"
              >
            </span>
            <span>收款人：{{ displayValue(receiptInfo.collectorName) }}</span>
            <span>制单人：{{ displayValue(receiptInfo.collectorName) }}</span>
            <span class="receipt-print-date">打印时间：{{ printDate }}</span>
          </footer>
          <div class="receipt-footer-tip">
            需要收款收据(押金除外)换发票的客户请在30日内凭此据到服务中心换取!
          </div>
        </article>
      </section>
    </main>

    <div
      v-if="receiptItems.length > 0"
      ref="receiptMeasurer"
      class="receipt-measurer no-print"
      aria-hidden="true"
    >
      <article
        ref="receiptMeasurerCopy"
        class="receipt-copy"
        :class="fontLevelClass(measureFontLevel)"
      >
        <header class="receipt-header">
          <div class="brand-placeholder">
            <img
              class="brand-image"
              src="@/assets/imgs/receipt-yh-logo.png"
              alt="怡和物业 YIHE PROPERTY"
            >
          </div>

          <div class="receipt-title">
            <h1>收 款 收 据</h1>
            <span>{{ displayValue(receiptInfo.receiptDate) }}</span>
          </div>

          <div class="receipt-number">
            <span class="copy-label">业主联</span>
            <span class="page-number">第 1/99 页</span>
            <strong>NO. {{ displayValue(receiptInfo.billNo) }}</strong>
          </div>
        </header>

        <div class="receipt-base-info">
          <div><span>项目：</span>{{ displayValue(receiptInfo.gardenName) }}</div>
          <div><span>房号：</span>{{ displayValue(receiptInfo.roomName) }}</div>
          <div><span>面积：</span>{{ formatArea(receiptInfo.buildingArea) }}</div>
        </div>

        <div class="customer-row">
          <span>客户名称：</span>{{ displayValue(receiptInfo.customerName) }}
        </div>

        <table class="receipt-table">
          <colgroup>
            <col class="fee-name-column">
            <col class="period-column">
            <col class="summary-column">
            <col class="amount-column">
            <col class="item-remark-column">
          </colgroup>
          <thead>
            <tr>
              <th>收费项目</th>
              <th>费用应收期间</th>
              <th>缴费摘要</th>
              <th>实收金额</th>
              <th>备注</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, rowIndex) in measureRows" :key="rowIndex" class="detail-row">
              <td>{{ displayValue(item.feeItemName) }}</td>
              <td>{{ displayValue(item.feeReceivablePeriod) }}</td>
              <td>{{ displayValue(item.paymentSummary) }}</td>
              <td class="money-cell">{{ formatMoney(item.receivedAmount) }}</td>
              <td class="item-remark-cell">{{ detailRemark(item) }}</td>
            </tr>
            <tr class="total-row">
              <th>整单合计金额</th>
              <td colspan="2">
                人民币（大写）：{{ amountToChinese(receiptInfo.totalReceivedAmount) }}
              </td>
              <td class="money-cell">{{ formatMoney(receiptInfo.totalReceivedAmount) }}</td>
              <td></td>
            </tr>
            <tr class="remark-row">
              <th>实收备注</th>
              <td colspan="4">{{ displayValue(receiptInfo.receiptRemark) }}</td>
            </tr>
          </tbody>
        </table>

        <footer class="receipt-footer">
          <span class="receipt-payee-unit">
            <span>收款单位：（盖章有效）</span>
            <img
              class="receipt-stamp"
              :src="receiptStamp.src"
              :alt="receiptStamp.alt"
            >
          </span>
          <span>收款人：{{ displayValue(receiptInfo.collectorName) }}</span>
          <span>制单人：{{ displayValue(receiptInfo.operatorName) }}</span>
          <span class="receipt-print-date">打印时间：{{ printDate }}</span>
        </footer>
        <div class="receipt-footer-tip">
          需要收款收据(押金除外)换发票的客户请在30日内凭此据到服务中心换取!
        </div>
      </article>
    </div>
  </div>
</template>

<script>
import defaultReceiptStamp from '@/assets/imgs/receipt-invoice-stamp.png'
import xishuangbannaReceiptStamp from '@/assets/imgs/receipt-xishuangbanna-invoice-stamp-crisp.png'

// 打印凭证有效期为 5 分钟，使用后立即从窗口和 Storage 中移除。
const RECEIPT_TRANSFER_PREFIX = 'feeReceiptPrint:'
const RECEIPT_TRANSFER_TTL = 5 * 60 * 1000
// 西双版纳项目使用独立的发票专用章。
const XISHUANGBANNA_GARDEN_IDS = ['387', '388', '389', '390']
// 单条备注过长时，只按这些等级逐步缩小备注文字。
const RECEIPT_FONT_LEVELS = ['normal', 'small', 'tiny', 'min']
const CHINESE_DIGITS = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖']
const SMALL_UNITS = ['', '拾', '佰', '仟']
const LARGE_UNITS = ['', '万', '亿', '兆']

// 将打印日期格式化为 yyyy-MM-dd。
function formatPrintDate(date) {
  const pad = value => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

// 将万以内的整数转换为人民币大写数字。
function convertFourDigits(value) {
  let result = ''
  let pendingZero = false

  for (let unitIndex = 3; unitIndex >= 0; unitIndex--) {
    const digit = Math.floor(value / Math.pow(10, unitIndex)) % 10
    if (digit === 0) {
      if (result) {
        pendingZero = true
      }
      continue
    }
    if (pendingZero) {
      result += CHINESE_DIGITS[0]
      pendingZero = false
    }
    result += CHINESE_DIGITS[digit] + SMALL_UNITS[unitIndex]
  }

  return result
}

// 将整数按万、亿等单位转换为人民币大写数字。
function integerToChinese(value) {
  if (value === 0) {
    return CHINESE_DIGITS[0]
  }

  const groups = []
  let remaining = value
  while (remaining > 0) {
    groups.push(remaining % 10000)
    remaining = Math.floor(remaining / 10000)
  }

  let result = ''
  let pendingZero = false
  for (let groupIndex = groups.length - 1; groupIndex >= 0; groupIndex--) {
    const groupValue = groups[groupIndex]
    if (groupValue === 0) {
      if (result) {
        pendingZero = true
      }
      continue
    }
    if (result && (pendingZero || groupValue < 1000)) {
      result += CHINESE_DIGITS[0]
    }
    result += convertFourDigits(groupValue) + LARGE_UNITS[groupIndex]
    pendingZero = false
  }

  return result
}

export default {
  name: 'FeeReceiptPrint',
  data() {
    return {
      loading: true,
      errorMessage: '',
      receiptNumber: '',
      gardenId: '',
      receiptItems: [],
      receiptPages: [],
      measureRows: [],
      measureFontLevel: RECEIPT_FONT_LEVELS[0],
      printDate: formatPrintDate(new Date()),
      receiptCopies: [
        { key: 'owner', label: '业主联' },
        { key: 'stub', label: '存根联' }
      ]
    }
  },
  computed: {
    // 获取收据公共信息，统一使用接口返回的第一条记录。
    receiptInfo() {
      return this.receiptItems[0] || {}
    },
    // 根据项目编号选择对应公司的发票专用章。
    receiptStamp() {
      const gardenId = this.normalizeText(this.gardenId || this.receiptInfo.gardenId)
      if (XISHUANGBANNA_GARDEN_IDS.indexOf(gardenId) >= 0) {
        return {
          src: xishuangbannaReceiptStamp,
          alt: '西双版纳怡和物业服务有限公司发票专用章'
        }
      }
      return {
        src: defaultReceiptStamp,
        alt: '昆明怡和物业服务有限公司发票专用章'
      }
    },
    // 判断当前收据是否满足打印条件。
    canPrint() {
      return !this.loading && !this.errorMessage && this.receiptPages.length > 0
    }
  },
  // 页面创建后先消费一次性凭证，再加载收据数据。
  created() {
    this.receiptNumber = this.consumeReceiptNumber()
    this.loadReceipt()
  },
  methods: {
    // 从一次性凭证中读取收据编号。
    consumeReceiptNumber() {
      const transferToken = this.normalizeText(this.$route.query.token)
      if (!/^[a-f0-9]{32}$/.test(transferToken)) {
        return ''
      }

      const storageKey = `${RECEIPT_TRANSFER_PREFIX}${transferToken}`
      // 新标签页优先从 window.name 读取一次性凭证，读取后立即清空。
      const windowPayload = window.name
      window.name = ''
      const receiptNumber = this.readTransferPayload(
        windowPayload,
        transferToken
      )
      if (receiptNumber) {
        try {
          window.sessionStorage.removeItem(storageKey)
          window.localStorage.removeItem(storageKey)
        } catch (error) {
          // 兼容凭证清理失败，不影响继续打印。
        }
        return receiptNumber
      }

      const sessionReceiptNumber = this.readTransferPayload(
        this.takeStoredTransferPayload(window.sessionStorage, storageKey),
        transferToken
      )
      if (sessionReceiptNumber) {
        return sessionReceiptNumber
      }

      const fallbackReceiptNumber = this.readTransferPayload(
        this.takeStoredTransferPayload(window.localStorage, storageKey),
        transferToken
      )
      if (fallbackReceiptNumber) {
        return fallbackReceiptNumber
      }

      return ''
    },
    // 读取并立即删除 Storage 中的一次性凭证。
    takeStoredTransferPayload(storage, storageKey) {
      if (!storage) {
        return ''
      }
      try {
        const storedValue = storage.getItem(storageKey)
        storage.removeItem(storageKey)
        return storedValue || ''
      } catch (error) {
        return ''
      }
    },
    // 解析并校验凭证内容、token 和有效期。
    readTransferPayload(storedValue, transferToken) {
      if (!storedValue) {
        return ''
      }
      try {
        const payload = JSON.parse(storedValue)
        const createdAt = Number(payload.createdAt)
        const age = Date.now() - createdAt
        if (
          payload.transferToken !== transferToken ||
          !createdAt ||
          age < 0 ||
          age > RECEIPT_TRANSFER_TTL
        ) {
          return ''
        }
        const receiptNumber = this.normalizeText(payload.receiptNumber)
        if (!receiptNumber) {
          return ''
        }
        // gardenId 随一次性凭证传递，不暴露在打印页地址栏中。
        this.gardenId = this.normalizeText(payload.gardenId)
        return receiptNumber
      } catch (error) {
        return ''
      }
    },
    // 请求接口并生成可打印的收据分页数据。
    loadReceipt() {
      if (!this.receiptNumber) {
        this.loading = false
        this.errorMessage = '打印凭证已失效，请从实收查询重新打开'
        return
      }

      this.loading = true
      this.errorMessage = ''
      this.receiptItems = []
      this.receiptPages = []
      this.measureRows = []
      this.$post('/combinedReceiptNumberV2', {
        receiptNumber: this.receiptNumber
      }).then(
        async res => {
          console.log(res)
          const list = res && Array.isArray(res.list) ? res.list : []
          if (!res || res.errCode !== 0) {
            this.errorMessage = (res && res.errMsg) || '收据数据加载失败'
            this.loading = false
          } else if (list.length === 0) {
            this.errorMessage = '未查询到可打印的收据数据'
            this.loading = false
          } else {
            this.receiptItems = list
            document.title = `${this.normalizeText(list[0].billNo) || '收据'}-打印`
            try {
              await this.buildAdaptivePages()
            } catch (error) {
              this.errorMessage = '收据分页计算失败，请重新加载'
            }
            this.loading = false
          }
        },
        error => {
          this.loading = false
          this.errorMessage = this.getErrorMessage(error)
        }
      )
    },
    // 根据每条明细的实际高度构建自适应分页。
    async buildAdaptivePages() {
      const pages = []
      let currentRows = []
      let currentFontLevel = RECEIPT_FONT_LEVELS[0]

      await this.$nextTick()

      // 逐条加入当前页并测量，下一条放不下时再开启新页。
      for (let index = 0; index < this.receiptItems.length; index++) {
        const item = this.receiptItems[index]
        const candidateRows = currentRows.concat(item)
        const candidateFit = await this.findFittingFontLevel(
          candidateRows,
          candidateRows.length === 1
        )

        if (candidateFit.fits) {
          currentRows = candidateRows
          currentFontLevel = candidateFit.fontLevel
          continue
        }

        if (currentRows.length > 0) {
          pages.push({
            rows: currentRows,
            fontLevel: currentFontLevel
          })
          currentRows = []
          currentFontLevel = RECEIPT_FONT_LEVELS[0]
          index -= 1
          continue
        }

        pages.push({
          rows: [item],
          fontLevel: candidateFit.fontLevel
        })
      }

      if (currentRows.length > 0) {
        pages.push({
          rows: currentRows,
          fontLevel: currentFontLevel
        })
      }

      this.receiptPages = pages
      this.measureRows = []
      this.measureFontLevel = RECEIPT_FONT_LEVELS[0]
      await this.$nextTick()
    },
    // 查找当前明细可容纳的备注字号等级。
    async findFittingFontLevel(rows, allowShrink) {
      // 只有单条明细仍放不下时才缩小备注，其他单元格字号不变。
      const fontLevels = allowShrink
        ? RECEIPT_FONT_LEVELS
        : [RECEIPT_FONT_LEVELS[0]]

      for (let index = 0; index < fontLevels.length; index++) {
        const fontLevel = fontLevels[index]
        const fits = await this.measureRowsFit(rows, fontLevel)
        if (fits) {
          return { fits: true, fontLevel }
        }
      }

      return {
        fits: false,
        fontLevel: fontLevels[fontLevels.length - 1]
      }
    },
    // 在隐藏收据联中测量指定明细是否会溢出。
    async measureRowsFit(rows, fontLevel) {
      this.measureRows = rows
      this.measureFontLevel = fontLevel
      await this.$nextTick()

      const copy = this.$refs.receiptMeasurerCopy
      if (!copy) {
        return false
      }

      // 隐藏收据联与正式收据尺寸一致，以实际渲染高度判断是否溢出。
      return copy.scrollHeight <= copy.clientHeight + 1
    },
    // 清理空值和字符串 NULL，避免页面显示无效文本。
    normalizeText(value) {
      if (value === null || value === undefined) {
        return ''
      }
      const text = String(value).trim()
      return text.toUpperCase() === 'NULL' ? '' : text
    },
    // 将字段值转换为可直接展示的文本。
    displayValue(value) {
      return this.normalizeText(value)
    },
    // 将备注字号等级转换为对应的 CSS 类名。
    fontLevelClass(fontLevel) {
      const level = RECEIPT_FONT_LEVELS.indexOf(fontLevel) >= 0
        ? fontLevel
        : RECEIPT_FONT_LEVELS[0]
      return `font-level-${level}`
    },
    // 从兼容字段中取得每条收费明细的备注。
    detailRemark(item) {
      if (!item) {
        return ''
      }
      // 兼容不同接口版本可能返回的明细备注字段名。
      const remarkKeys = ['itemRemark', 'detailRemark', 'feeRemark', 'remark', 'receiptRemark']
      for (let index = 0; index < remarkKeys.length; index++) {
        const text = this.normalizeText(item[remarkKeys[index]])
        if (text) {
          return text
        }
      }
      return ''
    },
    // 格式化建筑面积并补充平方米单位。
    formatArea(value) {
      const text = this.normalizeText(value)
      return text ? `${text}㎡` : ''
    },
    // 将金额格式化为两位小数。
    formatMoney(value) {
      const amount = Number(value)
      return Number.isFinite(amount) ? amount.toFixed(2) : '0.00'
    },
    // 将数字金额转换为人民币大写金额。
    amountToChinese(value) {
      const amount = Number(value)
      if (!Number.isFinite(amount)) {
        return '零元整'
      }

      const negative = amount < 0
      const cents = Math.round(Math.abs(amount) * 100)
      const integerPart = Math.floor(cents / 100)
      const jiao = Math.floor((cents % 100) / 10)
      const fen = cents % 10
      let result = `${integerToChinese(integerPart)}元`

      if (jiao === 0 && fen === 0) {
        result += '整'
      } else {
        if (jiao > 0) {
          result += `${CHINESE_DIGITS[jiao]}角`
        } else if (integerPart > 0 && fen > 0) {
          result += CHINESE_DIGITS[0]
        }
        if (fen > 0) {
          result += `${CHINESE_DIGITS[fen]}分`
        }
      }

      return negative ? `负${result}` : result
    },
    // 从请求异常中提取用户可读的错误信息。
    getErrorMessage(error) {
      if (typeof error === 'string' && error) {
        return error
      }
      if (error && error.message) {
        return error.message
      }
      return '收据数据加载失败，请稍后重试'
    },
    // 在数据准备完成后调用浏览器打印功能。
    printPage() {
      if (this.canPrint) {
        window.print()
      }
    },
    // 关闭当前打印页面。
    closePage() {
      window.close()
    }
  }
}
</script>

<style lang="scss" scoped>
.receipt-print-page {
  min-height: 100vh;
  padding: 24px 0 48px;
  box-sizing: border-box;
  color: #1f2933;
  background: #e9edf0;
  font-family: "Microsoft YaHei", "SimSun", sans-serif;
  letter-spacing: 0;
}

.print-toolbar {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  justify-content: center;
  gap: 12px;
  width: 210mm;
  margin: 0 auto 18px;
  padding: 12px 0;
  background: rgba(233, 237, 240, 0.96);
}

.page-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: min(720px, calc(100% - 32px));
  min-height: 240px;
  margin: 48px auto;
  background: #fff;
  border: 1px solid #d9e0e5;
  border-radius: 6px;
  font-size: 16px;
}

.page-state.page-error {
  flex-direction: column;
  color: #b42318;
}

.state-icon {
  font-size: 24px;
}

.receipt-document {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

/* 隐藏测量区复用正式收据宽度，仅用于计算自适应分页。 */
.receipt-measurer {
  position: absolute;
  top: 0;
  left: -9999px;
  width: 194mm;
  visibility: hidden;
  pointer-events: none;
}

.receipt-sheet {
  display: flex;
  flex-direction: column;
  gap: 5mm;
  width: 210mm;
  height: 297mm;
  padding: 7mm 8mm;
  box-sizing: border-box;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 4px 18px rgba(31, 41, 51, 0.14);
  break-inside: avoid;
  page-break-inside: avoid;
}

.receipt-copy {
  position: relative;
  flex: 0 0 136mm;
  height: 136mm;
  // padding: 2mm 3mm 0;
  box-sizing: border-box;
  color: #111;
  font-family: "SimSun", "Microsoft YaHei", sans-serif;
  font-size: 9pt;
  line-height: 1.25;
  break-inside: avoid;
  page-break-inside: avoid;
}

/* 仅上移业主联，存根联和中间裁切线保持原位。 */
.receipt-sheet > .receipt-copy:first-child {
  margin-top: -4mm;
  margin-bottom: 4mm;
}

.receipt-copy + .receipt-copy::before {
  position: absolute;
  top: -2.6mm;
  left: 0;
  width: 100%;
  border-top: 1px dashed #7d858c;
  content: "";
}

.receipt-header {
  display: grid;
  grid-template-columns: 1fr 1.4fr 1fr;
  align-items: end;
  min-height: 22mm;
  margin-bottom: 2mm;
  color: #111;
}

.brand-placeholder {
  display: flex;
  align-self: center;
  align-items: center;
}

.brand-image {
  display: block;
  width: 40mm;
  height: auto;
  object-fit: contain;
}

.receipt-title {
  text-align: center;
}

.receipt-title h1 {
  margin: 0 0 1.5mm;
  padding-bottom: 1mm;
  border-bottom: 1px solid #111;
  color: #111;
  font-size: 21pt;
  font-weight: 500;
  letter-spacing: 0;
}

.receipt-title span {
  font-size: 10pt;
}

.receipt-number {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1.4mm;
}

.copy-label {
  padding: 0.8mm 2mm;
  border: 1px solid #111;
  font-weight: 700;
}

.page-number {
  color: #111;
  font-size: 8pt;
}

.receipt-number strong {
  font-family: "Times New Roman", serif;
  font-size: 12pt;
}

.receipt-base-info {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr;
  align-items: center;
  min-height: 7mm;
  border-bottom: 1px solid #111;
  font-size: 10pt;
}

.receipt-base-info div:nth-child(2),
.receipt-base-info div:nth-child(3) {
  text-align: center;
}

.receipt-base-info span,
.customer-row span {
  font-weight: 700;
}

.customer-row {
  min-height: 7mm;
  padding: 1.5mm 2mm;
  box-sizing: border-box;
  border: 1px solid #111;
  border-top: 0;
  font-size: 10pt;
}

.receipt-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.receipt-copy.font-level-small .item-remark-cell {
  font-size: 8pt;
}

.receipt-copy.font-level-tiny .item-remark-cell {
  font-size: 7pt;
}

.receipt-copy.font-level-min .item-remark-cell {
  font-size: 6.5pt;
}

.receipt-table th,
.receipt-table td {
  padding: 0.8mm 1.2mm;
  overflow: hidden;
  border: 1px solid #111;
  overflow-wrap: anywhere;
  word-break: break-all;
  white-space: normal;
}

.receipt-table thead tr {
  height: 7mm;
}

.receipt-table thead th,
.total-row th,
.remark-row th {
  font-weight: 700;
  text-align: center;
}

.detail-row {
  height: 6mm;
  break-inside: avoid;
  page-break-inside: avoid;
}

.total-row {
  height: 8mm;
}

.remark-row {
  height: 8mm;
}

.fee-name-column {
  width: 20%;
}

.period-column {
  width: 20%;
}

.summary-column {
  width: 16%;
}

.amount-column {
  width: 10%;
}

.item-remark-column {
  width: 34%;
}

.money-cell {
  font-family: "Times New Roman", serif;
  text-align: left;
}

.receipt-footer {
  display: grid;
  grid-template-columns: 1.4fr 0.9fr 0.9fr 1.1fr;
  gap: 2mm;
  align-items: center;
  min-height: 8mm;
  padding: 1.5mm 2mm 0;
  box-sizing: border-box;
}

.receipt-print-date {
  white-space: nowrap;
}

.receipt-payee-unit {
  position: relative;
  display: inline-block;
  white-space: nowrap;
}

.receipt-stamp {
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 13mm;
  display: block;
  width: 36mm;
  height: 32mm;
  object-fit: contain;
  opacity: 0.86;
  pointer-events: none;
  transform: translateY(-60%);
}

/* 公章是覆盖效果，不应占用正文空间或影响分页条数。 */
.receipt-measurer .receipt-stamp {
  display: none;
}

.receipt-footer-tip {
  padding: 0 2mm;
  font-size: 8pt;
  line-height: 1.35;
  text-align: left;
}

</style>

<style lang="scss">
@page {
  size: A4 portrait;
  margin: 0;
}

@media print {
  html,
  body,
  #app {
    width: 210mm !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }

  .no-print {
    display: none !important;
  }

  .receipt-print-page {
    min-height: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }

  .receipt-document {
    display: block !important;
  }

  .receipt-sheet {
    margin: 0 !important;
    box-shadow: none !important;
    break-inside: avoid;
    break-after: page;
    page-break-inside: avoid;
    page-break-after: always;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .receipt-copy,
  .detail-row {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .receipt-sheet:last-child {
    break-after: auto;
    page-break-after: auto;
  }
}
</style>
